import { Box, ContentBlock, PageBlock } from 'braid-design-system';
import type { ReactNode } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { PAGE_ID_CLASSIFY } from 'src/app/shared/constants/pageUid';
import { REFERRER_CREATE_JOB } from 'src/app/shared/constants/tealium';
import ConfirmEmailAlert from 'src/app/v1/views/components/ConfirmEmailAlert';
import CopyJob from 'src/app/v1/views/components/CopyJob';
import ProgressBar from 'src/app/v1/views/components/ProgressBar';
import RoleReqsErrorMessage from 'src/app/v1/views/components/RoleReqsErrorMessage';
// @ts-ignore
import scrollToTop from 'src/app/v1/views/enhancers/scrollToTop';
import { useQueryParams } from 'src/app/v1/views/hooks/useQueryParams';
import PageErrorMessage from 'src/app/v1/views/shared/PageErrorMessage';
import type { AppState, PageErrorMessageType } from 'src/types/appState';
import type { DispatchDraftResponse } from 'src/types/draft';

import PageAlertMessage from './components/PageAlertMessage';
import { PromoCodeBanner } from './components/PromoCodeBanner';

export interface Props {
  belowContentArea?: ReactNode;
  children: ReactNode;
  countryCode: string;
  handleDispatchDraft: () => DispatchDraftResponse;
  handlePreSubmit?: () => boolean;
  handleProgrammaticRouteChange: (stepId: string) => void;
  handleValidation: () => string[];
  hasAside?: boolean;
  hasProgressBar: boolean;
  pageErrorMessage: PageErrorMessageType;
  rrFailedToSave?: boolean;
  isContentFullWidth?: boolean;
  headerContent?: ReactNode;
  beforeContent?: ReactNode;
  className?: string;
}

interface StateProps {
  hirerIsNew?: boolean;
  isEmailVerified: boolean;
  pageId?: string;
  referrer?: string;
}

export const PageLayout = ({
  belowContentArea,
  children,
  handleDispatchDraft,
  handlePreSubmit,
  handleProgrammaticRouteChange,
  handleValidation,
  hasProgressBar,
  pageErrorMessage,
  rrFailedToSave,
  headerContent,
  isContentFullWidth = false,
  beforeContent,
  className,
}: Props) => {
  const { hirerIsNew, isEmailVerified, pageId } = useSelector(
    (state: AppState): StateProps => ({
      hirerIsNew: state.session.hirerIsNew,
      isEmailVerified: state.session.isEmailVerified,
      pageId: state.session.pageId,
    }),
    shallowEqual,
  );
  const hasHeaderContent = headerContent || hasProgressBar;

  const { getReferrerParam } = useQueryParams();

  const referrerParam = getReferrerParam();

  return (
    <>
      <PromoCodeBanner />
      {hasHeaderContent && (
        <Box
          background="surface"
          paddingBottom="medium"
          paddingTop="xxlarge"
          paddingX="gutter"
        >
          {headerContent}
          {hasProgressBar && (
            <ProgressBar
              handleDispatchDraft={handleDispatchDraft}
              handlePreSubmit={handlePreSubmit}
              handleProgrammaticRouteChange={handleProgrammaticRouteChange}
              handleValidation={handleValidation}
            />
          )}
        </Box>
      )}
      <PageBlock>
        <ContentBlock width="medium">
          {isEmailVerified === false && (
            <PageAlertMessage>
              <ConfirmEmailAlert />
            </PageAlertMessage>
          )}
        </ContentBlock>
        {isContentFullWidth ? (
          <>{children}</>
        ) : (
          <ContentBlock>
            {rrFailedToSave && (
              <PageAlertMessage>
                <RoleReqsErrorMessage />
              </PageAlertMessage>
            )}

            {pageId === PAGE_ID_CLASSIFY &&
              !hirerIsNew &&
              referrerParam === REFERRER_CREATE_JOB && (
                <PageAlertMessage>
                  <CopyJob />
                </PageAlertMessage>
              )}

            <Box
              marginBottom="xxlarge"
              marginTop="xlarge"
              className={className}
            >
              {beforeContent}
              {children}
              <PageErrorMessage hasError={Boolean(pageErrorMessage)}>
                {pageErrorMessage}
              </PageErrorMessage>
              {belowContentArea}
            </Box>
          </ContentBlock>
        )}
      </PageBlock>
    </>
  );
};

export default scrollToTop(PageLayout);
